import type { PageQuery } from '@/shared/application/pagination/pagedItems';
import {
  AnalyzeParams,
  AssignBatchParams,
  BundleCorrection,
  ExtractOptions,
  IBatchProcessingRepository,
  VendorImportParams,
  VendorReconcileParams,
} from '../ports/outbound/IBatchProcessingRepository';
import {
  ReExtractAllProgress,
  RecountFindingsProgress,
  StreamEvent,
} from '../domain/entities/Digitization';

export class AnalyzeBatchUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(params: AnalyzeParams, onEvent: (e: StreamEvent) => void): Promise<void> {
    return this.repo.analyzeBatch(params, onEvent);
  }
}

export class ExtractBatchUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(
    batchId: string,
    onEvent: (e: StreamEvent) => void,
    options?: ExtractOptions
  ): Promise<void> {
    return this.repo.extractBatch(batchId, onEvent, options);
  }
}

export class RevalidateBatchUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string) {
    return this.repo.revalidateBatch(batchId);
  }
}

export class ListBatchesUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(page: PageQuery, options?: { excludeCompleted?: boolean }) {
    return this.repo.listBatches(page, options);
  }
}

export class FetchBatchUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string) {
    return this.repo.getBatch(batchId);
  }
}

export class CorrectBundleUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string, bundleId: string, correction: BundleCorrection) {
    return this.repo.patchBundle(batchId, bundleId, correction);
  }
}

/** Split pages out of a mis-grouped I-9 into a new one in the same batch (VTK-1930). */
export class SplitBundleUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string, bundleId: string, pages: number[]) {
    return this.repo.splitBundle(batchId, bundleId, pages);
  }
}

/** Turn pages that belong to no I-9 into a new one (the Grouping step's unassigned bucket). */
export class CreateBundleFromUnassignedPagesUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string, pages: number[]) {
    return this.repo.createBundleFromUnassignedPages(batchId, pages);
  }
}

export class PublishBatchUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string) {
    return this.repo.publishBatch(batchId);
  }
}

/** Ops Review queue (VTK-1834): bundles across the account with outstanding low-confidence reviews. */
export class GetOpsReviewQueueUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(page: PageQuery) {
    return this.repo.getOpsReviewQueue(page);
  }
}

/**
 * Digitized records the reverification rule cannot assess (VTK-1950). Super-admin only: an internal
 * data-quality view over records staff transcribed, not a client-facing list.
 */
export class GetReverificationGapsUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute() {
    return this.repo.getReverificationGaps();
  }
}

/** Immutable Ops Review audit trail for a bundle (VTK-1834). */
export class GetBundleReviewAuditUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string, bundleId: string) {
    return this.repo.getBundleReviewAudit(batchId, bundleId);
  }
}

/** The batch's own trail: ownership changes and grouping confirmation (not per-I-9 review history). */
export class GetBatchReviewAuditUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string) {
    return this.repo.getBatchReviewAudit(batchId);
  }
}

/** Digitize a stored file (VTK-1886): creates the batch + streams analyze/extract; emits batchId. */
export class DigitizeStoredFileUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(fileId: string, onEvent: (e: StreamEvent) => void) {
    return this.repo.digitizeStoredFile(fileId, onEvent);
  }
}

/**
 * VTK-1991 — import a prior vendor's I-9 export rather than scanning one.
 *
 * Deliberately a sibling of `AnalyzeBatchUseCase` rather than a different flow: both produce a
 * digitization batch, and everything after that — review, publish, audit — is the same path.
 */
export class ImportVendorExportUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(params: VendorImportParams, onEvent: (e: StreamEvent) => void) {
    return this.repo.importVendorExport(params, onEvent);
  }
}

/**
 * VTK-2153 — reconcile a client's imported cases with every delivery held for them. A sibling of
 * the import: a real run stages its rebuilds into ordinary batches for review.
 */
export class ReconcileVendorImportsUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(params: VendorReconcileParams, onEvent: (e: StreamEvent) => void) {
    return this.repo.reconcileVendorImports(params, onEvent);
  }
}

/** What deliveries are in the container for the selected client (VTK-1991). */
export class ListVendorDeliveriesUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(path?: string) {
    return this.repo.listVendorDeliveries(path);
  }
}

/** The worksites a delivery uses, with mapping + suggestion (VTK-1991). */
export class PreviewVendorWorksitesUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(documentPath: string) {
    return this.repo.previewVendorWorksites(documentPath);
  }
}

/** Save the operator's worksite mapping (VTK-1991). */
export class SaveVendorWorksitesUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(worksites: Array<{ code: string; label?: string | null; locationId?: string | null }>) {
    return this.repo.saveVendorWorksites(worksites);
  }
}

/** Confirm every record in a batch still awaiting a decision (VTK-1991). */
export class ConfirmAllBundlesUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(
    batchId: string,
    reason: string,
    onProgress?: (completed: number, total: number) => void
  ) {
    return this.repo.confirmAllBundles(batchId, reason, onProgress);
  }
}

/** Re-extract every batch in the account from its stored OCR (VTK-2073). */
export class ReExtractAllBatchesUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(onProgress?: (progress: ReExtractAllProgress) => void) {
    return this.repo.reExtractAllBatches(onProgress);
  }
}

/** Re-run the checks on every published digitized record (VTK-2192). */
export class RecountPublishedFindingsUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(dryRun: boolean, onProgress?: (progress: RecountFindingsProgress) => void) {
    return this.repo.recountPublishedFindings(dryRun, onProgress);
  }
}

/** Multi-batch: the records in the batches after this one, one page at a time. */
export class ListFollowingRecordsUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string, page: PageQuery) {
    return this.repo.listFollowingRecords(batchId, page);
  }
}

// ── Ownership / soft lock (VTK-1883) ────────────────────────────────────────
/** Claim / reassign / take over a batch (soft lock). */
export class AssignBatchUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string, params?: AssignBatchParams) {
    return this.repo.assignBatch(batchId, params);
  }
}

/** Lock the page grouping (VTK-1888) — owner confirms the split at the end of the Grouping step. */
export class LockGroupingUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string) {
    return this.repo.lockGrouping(batchId);
  }
}

/** Publish a single completed I-9 (VTK-1888) — explicit per-record publish / retry. */
export class PublishBundleUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(batchId: string, bundleId: string) {
    return this.repo.publishBundle(batchId, bundleId);
  }
}

/** "In progress / my assignments" queue: assigned + started, not yet published. */
export class GetInProgressQueueUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute(params?: { assignedTo?: string; accountId?: string }) {
    return this.repo.getInProgressQueue(params);
  }
}

/** Assignable internal (super-admin) users for the assignee picker. */
export class GetInternalUsersUseCase {
  constructor(private readonly repo: IBatchProcessingRepository) {}
  execute() {
    return this.repo.getInternalUsers();
  }
}
